import { api } from "@/redux/api";

import { ToDoTagInfo, ToDoTagInfoSchema } from "@/schemas/tag.schema";

import {
	CreateToDoInfo,
	DeleteToDoResponse,
	DeleteToDoResponseSchema,
	ToDoInfo,
	ToDoInfoSchema,
	ToDoResponseSchema,
	UpdateToDoInfo,
} from "../../pages/ToDo/ToDoList/schema/to-do";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformToDo = (toDo: any) => ({
	...toDo,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	industryIds: toDo.industries?.map((i: any) => i.id) ?? [],
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	companySizeIds: toDo.companySizes?.map((i: any) => i.id) ?? [],
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	companyRevenueIds: toDo.companyRevenues?.map((i: any) => i.id) ?? [],
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	companyBalanceIds: toDo.companyBalances?.map((i: any) => i.id) ?? [],
});

export const toDoApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getAllToDos: builder.query<ToDoInfo[], void>({
			query: () => ({
				url: "to-do/all",
				responseSchema: ToDoResponseSchema,
			}),
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			transformResponse: (response: any[]) => response.map(transformToDo),
			providesTags: ["ToDo"],
		}),
		getAllTags: builder.query<ToDoTagInfo[], void>({
			query: () => ({
				url: "to-do/tags",
				responseSchema: ToDoTagInfoSchema,
			}),
			providesTags: ["ToDoTags"],
		}),
		getToDosByFilters: builder.query<
			ToDoInfo[],
			{
				industryIds?: number[];
				companySizeIds?: number[];
				companyRevenueIds?: number[];
				companyBalanceIds?: number[];
			}
		>({
			query: (filters) => {
				const params = new URLSearchParams();

				if (filters.industryIds?.length) {
					params.append("industryIds", filters.industryIds.join(","));
				}

				if (filters.companySizeIds?.length) {
					params.append("companySizeIds", filters.companySizeIds.join(","));
				}

				if (filters.companyRevenueIds?.length) {
					params.append("companyRevenueIds", filters.companyRevenueIds.join(","));
				}

				if (filters.companyBalanceIds?.length) {
					params.append("companyBalanceIds", filters.companyBalanceIds.join(","));
				}

				return {
					url: `to-do/filter?${params.toString()}`,
					responseSchema: ToDoResponseSchema,
				};
			},
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			transformResponse: (response: any[]) => response.map(transformToDo),
			providesTags: ["ToDo"],
		}),
		createToDo: builder.mutation<ToDoInfo, CreateToDoInfo>({
			query: ({ subTasks, ...body }) => {
				return {
					url: `to-do`,
					method: "POST",
					body: { ...body, subTasks: JSON.stringify(subTasks) },
					responseSchema: ToDoInfoSchema,
				};
			},
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			transformResponse: transformToDo,
			invalidatesTags: ["CompanyToDoProgress"],
		}),

		updateToDo: builder.mutation<ToDoInfo, UpdateToDoInfo>({
			query: ({ subTasks, ...body }) => {
				return {
					url: `to-do/${body.id}`,
					method: "PATCH",
					body: { ...body, subTasks: JSON.stringify(subTasks) },
					responseSchema: ToDoInfoSchema,
				};
			},
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			transformResponse: transformToDo,
			invalidatesTags: ["CompanyToDoProgress"],
		}),

		deleteToDo: builder.mutation<DeleteToDoResponse, number>({
			query: (id) => ({
				url: `to-do/${id}`,
				method: "DELETE",
				responseSchema: DeleteToDoResponseSchema,
			}),
			invalidatesTags: ["CompanyToDoProgress"],
		}),
	}),
});

export const {
	useGetAllTagsQuery,
	useGetAllToDosQuery,
	useGetToDosByFiltersQuery,
	useLazyGetToDosByFiltersQuery,
	useCreateToDoMutation,
	useUpdateToDoMutation,
	useDeleteToDoMutation,
} = toDoApi;
export const companyToDoProgressReducer = toDoApi.reducer;
export const companyToDoProgressMiddleware = toDoApi.middleware;
